import { createContext, useContext, useState, useCallback, useEffect } from "react";
import useAxiosPrivate from "hooks/useAxiosPrivate";

const RegisterContext = createContext(null);

export const WITHDRAWAL_THRESHOLD = 1200;
export const SUGGESTED_WITHDRAWAL = 1000;

export function RegisterProvider({ children }) {
  const axiosPrivate = useAxiosPrivate();

  const [session, setSession] = useState(null);       // RegisterSessionRecord | null
  const [loading, setLoading] = useState(true);
  const [openModalVisible, setOpenModalVisible] = useState(false);
  const [closeModalVisible, setCloseModalVisible] = useState(false);
  const [withdrawalModalVisible, setWithdrawalModalVisible] = useState(false);
  const [withdrawalBannerDismissed, setWithdrawalBannerDismissed] = useState(false);

  const fetchCurrentSession = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosPrivate.get("/api/registers/current");
      setSession(res.data);
      setWithdrawalBannerDismissed(false); // reset banner dismissal on fresh data
    } catch (err) {
      if (err.response?.status === 428 || err.response?.status === 404) {
        setSession(null); // no open session — expected state, not an error
      } else {
        console.error("Failed to fetch register session", err);
      }
    } finally {
      setLoading(false);
    }
  }, [axiosPrivate]);

  useEffect(() => {
    fetchCurrentSession();
  }, [fetchCurrentSession]);

  // Poll every 30s so cash totals / threshold banner stay live during a shift
  useEffect(() => {
    if (!session) return;
    const interval = setInterval(fetchCurrentSession, 30000);
    return () => clearInterval(interval);
  }, [session, fetchCurrentSession]);

  const openRegister = useCallback(async ({ openingBalance, note }) => {
    const res = await axiosPrivate.post("/api/registers/open", { openingBalance, note });
    setSession(res.data);
    setOpenModalVisible(false);
    return res.data;
  }, [axiosPrivate]);

  const closeRegister = useCallback(async ({ countedAmount, note }) => {
    const res = await axiosPrivate.post("/api/registers/close", { countedAmount, note });
    setSession(null);
    setCloseModalVisible(false);
    return res.data;
  }, [axiosPrivate]);

  const requestWithdrawal = useCallback(async ({ amount, reason }) => {
    if (!session) throw new Error("No open session");
    const res = await axiosPrivate.post(`/api/withdrawals/session/${session.id}`, { amount, reason });
    await fetchCurrentSession(); // refresh totals
    setWithdrawalModalVisible(false);
    return res.data;
  }, [axiosPrivate, session, fetchCurrentSession]);

  const thresholdReached = session?.withdrawalThresholdReached && !withdrawalBannerDismissed;

  const value = {
    session,
    loading,
    hasOpenSession: !!session,
    thresholdReached,
    openModalVisible, setOpenModalVisible,
    closeModalVisible, setCloseModalVisible,
    withdrawalModalVisible, setWithdrawalModalVisible,
    dismissWithdrawalBanner: () => setWithdrawalBannerDismissed(true),
    fetchCurrentSession,
    openRegister,
    closeRegister,
    requestWithdrawal,
  };

  return <RegisterContext.Provider value={value}>{children}</RegisterContext.Provider>;
}

export function useRegister() {
  const ctx = useContext(RegisterContext);
  if (!ctx) throw new Error("useRegister must be used within a RegisterProvider");
  return ctx;
}